import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontWeight, Heading } from '@lili/lili-frontend-common';
import { useFormContext } from 'react-hook-form';
import { ApartmentInput } from '@Components/inputs/ApartmentInput';
import { Line1AddressInput } from '@Components/inputs/Line1Autocomplete/Line1AddressInput';
import { AddressStateInput } from '@Components/inputs/AddressStateDropDown';
import { ZipInput } from '@Components/inputs/ZipAddress';
import { CountryInput } from '@Components/inputs/AddressCountryAutoComplete/CountryInput';
import { getRouteOrdinal, RoutesMap } from '@Router/router.types';
import { CityInput } from '@Components/inputs/CityInput';
import { OnboardingFormFields } from '@Types/index';
import { extractStatesFromCountry, findCountry } from '@Services/utils/country';
import type { LiliDropdownItem, ListItem } from '@lili/lili-frontend-common';
import type { Country } from '@Types/country';
import type { UBOFormFields } from '../UBOFormScheme';

interface UBOHomeAddressProps {
  isUsResident: boolean;
  countriesList: LiliDropdownItem[];
  countries: Country[];
}

export const UBOHomeAddress = ({ isUsResident, countriesList, countries }: UBOHomeAddressProps) => {
  const { t } = useTranslation();
  const baseTabIndex = getRouteOrdinal(RoutesMap.ADD_OWNER);
  const formMethods = useFormContext<UBOFormFields>();

  const selectedAddressCountryCode = formMethods.watch(OnboardingFormFields.COUNTRY);
  const selectedCountry = selectedAddressCountryCode
    ? findCountry(countries, selectedAddressCountryCode.code)
    : undefined;

  const states = useMemo(() => {
    if (isUsResident) {
      const usCountry = findCountry(countries, 'US');
      return extractStatesFromCountry(usCountry) || [];
    }
    return extractStatesFromCountry(selectedCountry) || [];
  }, [isUsResident, selectedCountry, countries]);

  const showStates = !isUsResident && (states.length ?? 0) > 0;
  const isChildrenOfCountryShouldBeDisabled =
    (countriesList.length > 0 && !selectedCountry && !isUsResident) || false;

  const onCountryChange = (selectedCountryItem: ListItem, onChange: (value: ListItem) => void) => {
    const countryValue = {
      code: selectedCountryItem.value,
      name: selectedCountryItem.title,
    };
    // there's a type mismatch here, but it's because the country input expects a { code: string, name: string } and the onChange expects a ListItem
    // we have to align to this case until we fix the way country input works
    onChange(countryValue as unknown as ListItem);
    formMethods.setValue(OnboardingFormFields.STATE, null);
    formMethods.setValue(OnboardingFormFields.ZIP, '');
    formMethods.clearErrors(OnboardingFormFields.ZIP);
  };

  return (
    <div className='w-full'>
      <div className='flex justify-between items-center mb-[16px]'>
        <Heading.H8 fontWeight={FontWeight.MEDIUM}>
          {t('pages.addOwner.personalAddressSection.title')}
        </Heading.H8>
      </div>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-[24px]'>
        <Line1AddressInput
          isUsResident={isUsResident}
          disabled={false}
          tabIndex={baseTabIndex + 11}
          validStates={states}
        />
        <ApartmentInput tabIndex={baseTabIndex + 12} />

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-[24px] col-span-1 sm:col-span-2'>
          <CityInput tabIndex={baseTabIndex + 13} />

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-[24px]'>
            {isUsResident ? (
              <div data-testid='state_dropdown_wrapper'>
                <AddressStateInput tabIndex={baseTabIndex + 14} statesList={states} />
              </div>
            ) : (
              <div data-testid='country_input_wrapper'>
                {countriesList.length > 0 && (
                  <CountryInput
                    disabled={false}
                    tabIndex={baseTabIndex + 14}
                    countryList={countriesList}
                    onCountryChange={onCountryChange}
                  />
                )}
              </div>
            )}

            <ZipInput
              disabled={isChildrenOfCountryShouldBeDisabled}
              tabIndex={baseTabIndex + 15}
              isUS={isUsResident}
            />
          </div>
        </div>

        {showStates && <AddressStateInput tabIndex={baseTabIndex + 16} statesList={states} />}
      </div>
    </div>
  );
};
